import * as Yup from "yup";

import {
  emailRegex,
  nameRegex,
  numberStringOnly,
  alphanumericOnly,
} from "../FormComponents/Validations";
import { validPhoneNumber } from "../helper";
import isValidABN from "is-valid-abn";
import { getIsElectricityAndGas, shouldHidePoBoxNumber } from "./helper";
import { ConnectionTypeEnum, State } from "src/components/Premises/types";
import { levelSchema } from "src/schemas/LevelSchema";
import { isAllowedConcessionCardType } from "./concessionCardTypes";
import { shouldShowServicesAustraliaConsent } from "./concessionConsentScripts";

const suburbRegex = /^[a-zA-Z0-9- ]+$/;
const suburbRegexWithoutNumbers = /^[a-zA-Z- ]+$/;
const krakenStreetNumberRegex =
  /^(?:[a-zA-Z]\d+|\d+[a-zA-Z]?)(?:-(?:[a-zA-Z]\d+|\d+[a-zA-Z]?))?$/;
const streetNumberErrorMessage = "Numbers, single letters and hyphens only";

// Validation schemas for each page
export const pageOneValidationSchema = Yup.object({
  referenceNumber: Yup.string()
    .required("Oops, you must enter a request ID.")
    .matches(
      /^[Oo][Cc]\d{6,}$/,
      "Oops, request ID should be OC followed by 6 or more digits."
    )
    .test("check-details-validation", function validateReferenceNumber(value) {
      if (!value) return true;
      if (!/^[Oo][Cc]\d{6,}$/.test(value)) {
        return this.createError({
          message:
            "Oops, request ID should be OC followed by 6 or more digits.",
        });
      }
      return true;
    }),
  postcode: Yup.string()
    .required("Oops, you must enter a postcode.")
    .matches(/^\d{4}$/, "Oops, postcodes should be 4 digits.")
    .test("check-details-validation", function validatePostcode(value) {
      if (!value) return true;
      if (!/^\d{4}$/.test(value)) {
        return this.createError({
          message: "Oops, postcodes should be 4 digits.",
        });
      }
      return true;
    }),
});

export const pageTwoValidationSchema = Yup.object({
  firstName: Yup.string()
    .matches(nameRegex, "Letters, hyphens, spaces and apostrophes only")
    .required("Oops, please enter a first name"),
  lastName: Yup.string()
    .matches(nameRegex, "Letters, hyphens, spaces and apostrophes only")
    .required("Oops, please enter a last name"),
  dateOfBirth: Yup.mixed().when("accountType", {
    is: (accountType: string) => accountType === "personal",
    then: (schema) =>
      schema
        .test("is-valid-date", "Date format should be DD/MM/YYYY", (value) => {
          if (value === null) return false; // Fail if null (required)
          return value instanceof Date && !isNaN(value.getTime());
        })
        .required("Oops, please enter date of birth"),
    otherwise: (schema) => schema.nullable(),
  }),
  phone: Yup.string()
    .test({
      message: "Please enter a valid 10 digit phone number",
      test: (value) => validPhoneNumber(value as string),
    })
    .matches(numberStringOnly, "Please enter a valid 10 digit phone number")
    .required("Oops, please enter a phone number"),

  email: Yup.string()
    .matches(emailRegex, "Please enter a valid email address")
    .required("Oops, please enter an email address"),

  // Business account holder fields
  companyName: Yup.string().when("accountType", {
    is: "business",
    then: (schema) => schema.required("Oops, please enter a company name"),
    otherwise: (schema) => schema,
  }),
  abn: Yup.string().when("accountType", {
    is: "business",
    then: (schema) =>
      schema
        .test({
          message: "Please enter a valid 11 digit ABN number",
          test: (value) => isValidABN(value as string),
        })
        .required("Please enter an ABN number"),
    otherwise: (schema) => schema,
  }),

  // Secondary account holder fields
  addSecondaryAccountHolder: Yup.boolean(),
  secondaryFirstName: Yup.string().when(
    ["accountType", "addSecondaryAccountHolder"],
    {
      is: (accountType: string, addSecondary: boolean) =>
        accountType === "personal" && addSecondary === true,
      then: (schema) =>
        schema
          .matches(nameRegex, "Letters, hyphens, spaces and apostrophes only")
          .required("Oops, please enter a first name"),
      otherwise: (schema) => schema,
    }
  ),
  secondaryLastName: Yup.string().when(
    ["accountType", "addSecondaryAccountHolder"],
    {
      is: (accountType: string, addSecondary: boolean) =>
        accountType === "personal" && addSecondary === true,
      then: (schema) =>
        schema
          .matches(nameRegex, "Letters, hyphens, spaces and apostrophes only")
          .required("Oops, please enter a last name"),
      otherwise: (schema) => schema,
    }
  ),
  secondaryDateOfBirth: Yup.mixed().when(
    ["accountType", "addSecondaryAccountHolder"],
    {
      is: (accountType: string, addSecondary: boolean) =>
        accountType === "personal" && addSecondary === true,
      then: (schema) =>
        schema
          .test(
            "is-valid-date",
            "Date format should be DD/MM/YYYY",
            (value) => {
              if (value === null) return false; // Fail if null (required)
              return value instanceof Date && !isNaN(value.getTime());
            }
          )
          .required("Oops, please enter date of birth"),
      otherwise: (schema) => schema.nullable(),
    }
  ),
  secondaryPhone: Yup.string().when(
    ["accountType", "addSecondaryAccountHolder"],
    {
      is: (accountType: string, addSecondary: boolean) =>
        accountType === "personal" && addSecondary === true,
      then: (schema) =>
        schema
          .test({
            message: "Please enter a valid 10 digit phone number",
            test: (value) => validPhoneNumber(value as string),
          })
          .matches(
            numberStringOnly,
            "Please enter a valid 10 digit phone number"
          )
          .required("Oops, please enter a phone number"),
      otherwise: (schema) => schema,
    }
  ),
  secondaryEmail: Yup.string().when(
    ["accountType", "addSecondaryAccountHolder"],
    {
      is: (accountType: string, addSecondary: boolean) =>
        accountType === "personal" && addSecondary === true,
      then: (schema) =>
        schema
          .matches(emailRegex, "Please enter a valid email address")
          .required("Oops, please enter an email address"),
      otherwise: (schema) => schema,
    }
  ),
});
// Validation schema for Concession block fields (used in page 3 and page 5).
// concessionStatus is boolean per BE contract (true=yes, false=no, null=unset).
const concessionFieldRules = {
  concessionStatus: Yup.boolean()
    .typeError("Please answer the concession card question")
    .required("Please answer the concession card question"),
  // Card type is captured per-state. SA has no card-type capture (dropdown
  // hidden in ConcessionBlock), so skip the required rule there.
  concessionCardType: Yup.string().when(
    ["concessionStatus", "state"],
    ([status, state], schema) => {
      if (status !== true || state === State.SA) return schema;

      return schema
        .required("Please select a concession card type")
        .test(
          "allowed-concession-card-type",
          "Please select a valid concession card type",
          (value) =>
            !value || isAllowedConcessionCardType(state as State | "", value)
        );
    }
  ),
  // SA does not collect a card number or require consent
  concessionNumber: Yup.string().when(
    ["concessionStatus", "state"],
    ([status, state], schema) =>
      status === true && state !== State.SA
        ? schema
            .matches(alphanumericOnly, "Concession number must be alphanumeric")
            .required("Please enter your concession card number")
        : schema
  ),
  consentElectronicSignature: Yup.boolean().when(
    ["concessionStatus", "state"],
    ([status, state], schema) =>
      status === true && state !== State.SA
        ? schema.oneOf(
            [true],
            "You must confirm the electronic signature consent"
          )
        : schema
  ),
  consentLegallyBinding: Yup.boolean().when(
    ["concessionStatus", "state"],
    ([status, state], schema) =>
      status === true && state !== State.SA
        ? schema.oneOf([true], "You must confirm the legally binding consent")
        : schema
  ),
};

// FE validation only for Services Australia consent depending on state and card type
const servicesAustraliaConsentRules = {
  consentElectronicSignatureServicesAustralia: Yup.boolean().when(
    ["concessionStatus", "state", "concessionCardType"],
    ([status, state, cardType], schema) =>
      status === true && shouldShowServicesAustraliaConsent(state, cardType)
        ? schema.oneOf(
            [true],
            "You must confirm the electronic signature consent"
          )
        : schema
  ),
  consentLegallyBindingServicesAustralia: Yup.boolean().when(
    ["concessionStatus", "state", "concessionCardType"],
    ([status, state, cardType], schema) =>
      status === true && shouldShowServicesAustraliaConsent(state, cardType)
        ? schema.oneOf([true], "You must confirm the legally binding consent")
        : schema
  ),
};

export const pageThreeValidationSchema = Yup.object({
  ...concessionFieldRules,
  ...servicesAustraliaConsentRules,
  billDeliveryMethod: Yup.string().required("Please select a delivery method"),
  postalAddressType: Yup.string().required(
    "Please select a postal address type"
  ),
  lifeSupport: Yup.string().when(["connectionTypes", "$state"], {
    is: (connectionTypes: ConnectionTypeEnum[], state: string) =>
      (connectionTypes.length === 1 &&
        connectionTypes[0] === ConnectionTypeEnum.SERVICE_CONNECTION) ||
      state !== State.VIC,
    then: (schema) => schema,
    otherwise: (schema) =>
      schema.required("Please select a life support option"),
  }),
  whichEnergy: Yup.string().when(["connectionTypes", "lifeSupport"], {
    is: (connectionTypes: ConnectionTypeEnum[], lifeSupport: string) =>
      getIsElectricityAndGas(connectionTypes) && lifeSupport === "yes",
    then: (schema) => schema.required("Please select an energy type"),
    otherwise: (schema) => schema,
  }),

  // Postal address fields validation
  postalUnitNo: Yup.string().when(["postalAddressType", "postalPremiseType"], {
    is: (postalAddressType: string, postalPremiseType: string) =>
      postalAddressType === "postalAddress" && postalPremiseType,
    then: (schema) =>
      schema
        .matches(alphanumericOnly, "Numbers and letters only")
        .required("Oops, please enter a unit/premise number"),
    otherwise: (schema) => schema,
  }),
  // Premise type is mandatory once a unit/premise number is entered (LOC-1614).
  // Uses a test reading sibling values so it adds no Yup dependency edge, avoiding a
  // cyclic dependency with the postalUnitNo rule above.
  postalPremiseType: Yup.string().test(
    "premise-type-required-with-unit",
    "Oops, please select a premise type",
    function validatePremiseType(value) {
      const { postalAddressType, postalUnitNo } = this.parent;
      if (postalAddressType === "postalAddress" && postalUnitNo) {
        return !!value;
      }
      return true;
    }
  ),
  postalLevelNo: levelSchema,
  postalStreetNo: Yup.string()
    .max(10, streetNumberErrorMessage)
    .when("postalAddressType", {
      is: "postalAddress",
      then: (schema) =>
        schema
          .matches(krakenStreetNumberRegex, streetNumberErrorMessage)
          .required("Oops, please enter a street number"),
      otherwise: (schema) => schema,
    }),
  postalStreetName: Yup.string().when("postalAddressType", {
    is: "postalAddress",
    then: (schema) =>
      schema
        .matches(suburbRegex, "Numbers, letters, hyphens and spaces only")
        .required("Oops, please enter a street name"),
    otherwise: (schema) => schema,
  }),
  postalStreetType: Yup.string().when("postalAddressType", {
    is: "postalAddress",
    then: (schema) => schema.required("Oops, please enter a street type"),
    otherwise: (schema) => schema,
  }),
  postalSuffix: Yup.string().when("postalAddressType", {
    is: "postalAddress",
    then: (schema) => schema,
    otherwise: (schema) => schema,
  }),
  postalSuburb: Yup.string().when("postalAddressType", {
    is: "postalAddress",
    then: (schema) =>
      schema
        .matches(suburbRegexWithoutNumbers, "Letters, hyphens and spaces only")
        .required("Oops, please enter a suburb"),
    otherwise: (schema) => schema,
  }),
  postalState: Yup.string().when("postalAddressType", {
    is: "postalAddress",
    then: (schema) =>
      schema
        .matches(alphanumericOnly)
        .min(2)
        .max(3)
        .required("Oops, please enter a state"),
    otherwise: (schema) => schema,
  }),
  postalPostcode: Yup.string().when("postalAddressType", {
    is: "postalAddress",
    then: (schema) =>
      schema
        .matches(numberStringOnly, "Numbers only")
        .min(4, "Postcode must be at least 4 characters")
        .max(4)
        .required("Oops, please enter a postal address"),
    otherwise: (schema) => schema,
  }),

  // PO Box fields validation
  poBoxType: Yup.string().when("postalAddressType", {
    is: "poBox",
    then: (schema) => schema.required("Oops, please enter a box type"),
    otherwise: (schema) => schema,
  }),
  poBoxNo: Yup.string().when(["postalAddressType", "poBoxType"], {
    is: (postalAddressType: string, poBoxType: string) =>
      postalAddressType === "poBox" &&
      poBoxType &&
      !shouldHidePoBoxNumber(poBoxType),
    then: (schema) =>
      schema
        .matches(numberStringOnly, "Numbers only")
        .required("Oops, please enter a PO box number"),
    otherwise: (schema) => schema,
  }),
  poBoxSuburb: Yup.string().when("postalAddressType", {
    is: "poBox",
    then: (schema) =>
      schema
        .matches(suburbRegexWithoutNumbers, "Letters, hyphens and spaces only")
        .required("Oops, please enter a suburb"),
    otherwise: (schema) => schema,
  }),
  poBoxState: Yup.string().when("postalAddressType", {
    is: "poBox",
    then: (schema) =>
      schema
        .matches(alphanumericOnly)
        .min(2)
        .max(3)
        .required("Oops, please enter a state"),
    otherwise: (schema) => schema,
  }),
  poBoxPostcode: Yup.string().when("postalAddressType", {
    is: "poBox",
    then: (schema) =>
      schema
        .matches(numberStringOnly, "Numbers only")
        .min(4, "Postcode must be at least 4 characters")
        .max(4)
        .required("Oops, please enter a postcode"),
    otherwise: (schema) => schema,
  }),
});

// Validation schema for readonly page 3 - only validates editable fields (life support).
// Concession is intentionally excluded: on the readonly return flow the customer applied
export const pageThreeReadOnlyValidationSchema = Yup.object({
  lifeSupport: Yup.string().when(["connectionTypes", "$state"], {
    is: (connectionTypes: ConnectionTypeEnum[], state: string) =>
      (connectionTypes.length === 1 &&
        connectionTypes[0] === ConnectionTypeEnum.SERVICE_CONNECTION) ||
      state !== State.VIC,
    then: (schema) => schema,
    otherwise: (schema) =>
      schema.required("Please select a life support option"),
  }),
  whichEnergy: Yup.string().when(["connectionTypes", "lifeSupport"], {
    is: (connectionTypes: ConnectionTypeEnum[], lifeSupport: string) =>
      getIsElectricityAndGas(connectionTypes) && lifeSupport === "yes",
    then: (schema) => schema.required("Please select an energy type"),
    otherwise: (schema) => schema,
  }),
});
